import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from 'constants/AuthConstant';
import { notification } from 'antd';
import { resetWsAuth, closeWS } from '../wsClient';

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh the access token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken
    });

    const { accessToken, refreshToken: newRefreshToken, user } = response.data;

    // Save new tokens
    localStorage.setItem(AUTH_TOKEN, accessToken);
    if (newRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
    }
    if (user) {
      localStorage.setItem(USER_INFO, JSON.stringify(user));
    }

    // Reset WebSocket auth so it can reconnect with new token
    resetWsAuth();

    return accessToken;
  } catch (error) {
    // Refresh failed - clear everything and logout
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(USER_INFO);
    closeWS();
    throw error;
  }
};

// API Request interceptor
service.interceptors.request.use(
  config => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN);
    
    if (jwtToken) {
      config.headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  error => {
    notification.error({
      message: 'Request Error',
      description: error.message
    });
    return Promise.reject(error);
  }
);

// API Response interceptor
service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if it's a token expired error
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return service(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return service(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Refresh failed - logout user
          notification.error({
            message: 'Session Expired',
            description: 'Please login again'
          });
          store.dispatch(signOutSuccess());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Other 401 errors - logout
      notification.error({
        message: 'Authentication Failed',
        description: 'Please login again'
      });
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      localStorage.removeItem(USER_INFO);
      closeWS();
      store.dispatch(signOutSuccess());
      return Promise.reject(error);
    }

    // Handle 403 - Forbidden
    if (error.response?.status === 403) {
      notification.error({
        message: 'Access Denied',
        description: error.response?.data?.message || 'You do not have permission to perform this action'
      });
      return Promise.reject(error);
    }

    // Handle other errors
    let notificationParam = {
      message: 'Error',
      description: error.response?.data?.message || error.message
    };

    if (error.response?.status === 404) {
      notificationParam.message = 'Not Found';
    }

    if (error.response?.status === 500) {
      notificationParam.message = 'Internal Server Error';
    }

    if (error.response?.status === 508) {
      notificationParam.message = 'Request Timeout';
    }

    // Don't show notification for cancelled requests
    if (!axios.isCancel(error)) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

export default service;
